import {map} from 'lodash';
import moment from 'moment';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Chart} from 'components';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {formatSeconds} from 'utils/transform';

import {REQUEST_SESSION_HISTORY_DATE_FORMAT} from '../../constants';
import {ActivityChartType, ActivityMetric, ActivityMetricColor} from '../../enums';
import {AgentStatistic} from '../../types';

const T_KEYS_STATISTICS = TRANSLATION_KEYS.USER.ACTIVITY.STATISTICS;

type Props = {
    statistic: AgentStatistic;
    chartType: ActivityChartType;
    dates: string[];
    format: string;
};

export const ActivityStatisticChart: React.FC<Props> = ({dates, statistic, chartType, format}: Props) => {
    const {t} = useTranslation();
    const chartData = map(dates, (date) => ({
        date: moment(date, REQUEST_SESSION_HISTORY_DATE_FORMAT).format(format),
        ...(statistic.days[date] || {answered: 0, avgDuration: 0})
    }));

    const [areas, setAreas] = useState([]);
    const [lines, setLines] = useState([]);
    const [yAxis, setYAxis] = useState({});

    useEffect(() => {
        if (chartType === ActivityChartType.FilePipelineStatus || chartType === ActivityChartType.SessionResult) {
            const {success, fail} =
                chartType === ActivityChartType.SessionResult ?
                    {
                        success: {dataKey: ActivityMetric.Successful, name: t(T_KEYS_STATISTICS.SUCCESSFUL.TITLE)},
                        fail: {dataKey: ActivityMetric.Failed, name: t(T_KEYS_STATISTICS.FAILED.TITLE)}
                    } :
                    {
                        success: {
                            dataKey: ActivityMetric.AllFilesProcessed,
                            name: t(T_KEYS_STATISTICS.ALL_FILES_PROCESSED.TITLE)
                        },
                        fail: {
                            dataKey: ActivityMetric.SomeFilesUnprocessed,
                            name: t(T_KEYS_STATISTICS.SOME_FILES_UNPROCESSED.TITLE)
                        }
                    };
            setAreas([
                {
                    type: 'monotone', stackId: '1', dataKey: fail.dataKey,
                    name: fail.name, stroke: ActivityMetricColor[ActivityMetric.Failed],
                    fill: ActivityMetricColor[ActivityMetric.Failed], fillOpacity: 1
                },
                {
                    type: 'monotone', stackId: '1', dataKey: success.dataKey, fillOpacity: 1,
                    name: success.name, stroke: ActivityMetricColor[ActivityMetric.Successful],
                    fill: ActivityMetricColor[ActivityMetric.Successful]
                },
            ]);
        } else {
            // Just for animation
            setAreas([{type: 'monotone', stackId: '1', dataKey: ''}, {type: 'monotone', stackId: '1', dataKey: ''}]);
        }

        if (chartType === ActivityChartType.AvgDuration) {
            setLines([{
                type: 'monotone', dataKey: ActivityMetric.AvgDuration, name: t(T_KEYS_STATISTICS.AVG_DURATION.TITLE) as string,
                stroke: '#33b5e5', dot: false, formatter: (value) => formatSeconds(value),
            }]);
            setYAxis({dataKey: ActivityMetric.AvgDuration, width: 100, tickFormatter: (value) => formatSeconds(value)});
        } else {
            setLines([{
                type: 'monotone', dataKey: ActivityMetric.Answered, name: t(T_KEYS_STATISTICS.ANSWERED.TITLE) as string,
                stroke: '#33b5e5', dot: false,
            }]);
            setYAxis({dataKey: ActivityMetric.Answered});
        }
    }, [t, chartType]);

    return (
        <Chart
            tooltip={{active: true}}
            margin={{top: 20, right: 20, left: 10, bottom: 5}}
            grid={{stroke: '#eeeeee'}}
            xAxis={{dataKey: 'date'}}
            yAxis={yAxis}
            lines={lines}
            areas={areas}
            data={chartData}
        />
    );
};